import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from '@floating-ui/vue'
import { onClickOutside } from '@vueuse/core'

import type { CSSProperties, ComputedRef, Ref } from 'vue'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { Team } from '../interfaces'

import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons'

import { useSidebarPrivileges } from '@entities/viewer'

import { useTeamsStore } from './store'
import { getTeamEditPath } from './team-options'

interface UseTeamActionsOptions {
  team: ComputedRef<Team>
}

interface TeamActionsReturn {
  isOpen: Ref<boolean>
  containerRef: Ref<HTMLElement | null>
  floatingRef: Ref<HTMLElement | null>
  floatingStyles: Ref<CSSProperties>
  triggerIcon: IconDefinition
  canManage: ComputedRef<boolean>
  toggle: () => void
  openEdit: () => Promise<void>
  openDelete: () => void
}

export const useTeamActions = ({ team }: UseTeamActionsOptions): TeamActionsReturn => {
  const router = useRouter()
  const store = useTeamsStore()
  const { canManageTeams } = useSidebarPrivileges()

  const isOpen = ref(false)
  const containerRef = ref<HTMLElement | null>(null)
  const floatingRef = ref<HTMLElement | null>(null)

  const { floatingStyles } = useFloating(containerRef, floatingRef, {
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift({ padding: 8 }),
    ],
  })

  const canManage = computed<boolean>(() => canManageTeams.value)

  const toggle = (): void => {
    isOpen.value = !isOpen.value
  }

  const openEdit = async (): Promise<void> => {
    isOpen.value = false
    await router.push(getTeamEditPath(team.value.id))
  }

  const openDelete = (): void => {
    isOpen.value = false
    store.setTeamToDelete(team.value)
  }

  onClickOutside(containerRef, () => {
    isOpen.value = false
  })

  return {
    isOpen,
    containerRef,
    floatingRef,
    floatingStyles,
    triggerIcon: faEllipsisVertical,
    canManage,
    toggle,
    openEdit,
    openDelete,
  }
}
